/* Work closing.

   The opening flight, played backwards. Opening sent the stack away, flew the
   clicked row onto the case-study header and flooded the accent down out of
   it. Closing undoes each of those in reverse order:

   1. the body text drops out, fast
   2. the accent page retracts upward into the header band — the same wash
      geometry as the flood, run the other way
   3. the band travels back to the row's own position in the overview
   4. only then the real navigation happens, and work-opening.js flies the rest
      of the stack back in around the row that just landed

   Step 3 needs a position this document cannot measure, so the overview left
   it in sessionStorage on the way in (see remember() in work-opening.js).
   Without that note — a case study opened directly, or a reload — the page
   just collapses in place, which is still a close.

   Same contract as the opening: the arrow stays a real link, so reduced
   motion, middle-click and no JavaScript all fall through to the anchor. */
function workClosing() {
    var root = document.documentElement;
    var main = document.querySelector('.gh-work-case-study-main');
    var band = document.querySelector('.gh-work-case-study-intro');
    var scrollport = document.querySelector('[data-scrollport]');
    var closing = false;

    function cssMs(name, fallback) {
        var declared = parseInt(
            window.getComputedStyle(root).getPropertyValue(name),
            10
        );
        return isNaN(declared) ? fallback : declared;
    }

    /* The note is read but no longer consumed.

       Deleting it on first use meant one journey home per opening, and every
       other way of standing on a case study had none: reopened from the
       bfcache, or simply reloaded. Those closes fell to the unmeasured branch —
       the accent folded up to a white page, and because that branch also writes
       no note, the overview then popped in assembled with no stack flying in.
       That is the reported "white screen, then the page appears out of nowhere".

       Keeping the note makes those closes the ordinary close. It is keyed by
       URL so a note left by a different case study is never flown to. */
    function takeReturn() {
        var raw;
        try {
            raw = sessionStorage.getItem('mk-work-return');
        } catch (e) {
            return null;
        }
        if (!raw) return null;

        try {
            var saved = JSON.parse(raw);
            // url is absent on a note written by an older build; those stay
            // usable rather than dropping such a tab to the fold-up close.
            if (saved.url && saved.url !== window.location.href) return null;
            return typeof saved.top === 'number' && typeof saved.index === 'number'
                ? saved
                : null;
        } catch (e) {
            return null;
        }
    }

    // The flood, inverted. Opening laid the wash where the band would land and
    // grew it down from the row's bottom edge; closing lays it at the band's
    // bottom edge and shrinks it back to where that edge is going.
    function retractBackground(bandRect, landingBottom) {
        var wash = document.createElement('div');
        wash.className = 'gh-work-opening-wash is-expanded';
        wash.style.top = bandRect.bottom + 'px';
        wash.style.setProperty('--wash-from', (landingBottom - bandRect.bottom) + 'px');
        // The wash hangs off <body>, and the accent is declared on <main>, so
        // it has to be carried over rather than inherited.
        wash.style.setProperty(
            '--work-accent',
            window.getComputedStyle(main).getPropertyValue('--work-accent')
        );
        wash.style.setProperty(
            '--work-opening-duration-ms',
            cssMs('--work-closing-duration-ms', 320) + 'ms'
        );

        document.body.appendChild(wash);
        wash.getBoundingClientRect(); // start frame for the scale transition
        wash.classList.remove('is-expanded');
    }

    /* Leaving a case study for somewhere that is not the overview — About,
       Kontakt — closes it the same way, minus the part that only means anything
       on the overview. page-transition.js calls this instead of its own fade.

       Deliberately the unmeasured close: the accent page folds up into the nav
       line it grew from, which is already the branch for "there is no row to fly
       back to". Flying the band to a saved row offset would be aiming at a
       position that does not exist on the page being opened.

       It rides back to the top first, same as the measured close: the fold
       collapses <main> toward its own top edge, which from deep in a case study
       is somewhere above the viewport — the visible text slides up into nothing
       and the nav line it is supposed to fold into is never part of the motion.
       So `started` is called once the fold actually begins, and page-transition.js
       starts its navigation clock from there rather than from the click. */
    window.mikaWorkFoldUp = function (started) {
        if (!main) return false;
        rideToTop(function () {
            collapse(null);
            if (started) started();
        });
        return true;
    };

    /* Closing from deep in the case study used to shut the page around a band
       that was scrolled off the top of the viewport: the accent retracted into
       an edge nobody could see, and the fly-back swooped in from above out of
       nowhere. Ride back up first, then close, so the band is on screen for the
       whole motion. Everything below the wait already reduces to the old
       behaviour, because after this the scrollport is at the top. */
    function rideToTop(done) {
        var from = scrollport ? scrollport.scrollTop : window.scrollY;
        if (from < 2) return done();

        /* Tweened here rather than handed to `behavior: 'smooth'`. The browser
           owns that duration and spends the better part of a second on it from
           deep in a long case study — longer than the close it is only the
           run-up to, and unwatchable twice. This scales with the distance and
           caps well under the closing duration, so the ride reads as part of
           the same gesture. Ease-out, because it arrives into a motion that
           starts immediately. */
        var ms = Math.min(120 + from * 0.2, 380);
        var start = 0;

        window.requestAnimationFrame(function step(now) {
            if (!start) start = now;

            var t = Math.min((now - start) / ms, 1);
            var y = from * Math.pow(1 - t, 3);

            if (scrollport) scrollport.scrollTop = y;
            else window.scrollTo(0, y);

            if (t < 1) return window.requestAnimationFrame(step);
            done();
        });
    }

    function collapse(saved) {
        root.classList.add('is-work-closing');
        if (!saved || !main || !band) return;

        // The saved value is the viewport position the row has in an unscrolled
        // overview scrollport, which is where the browser will put us on arrival.
        var rect = band.getBoundingClientRect();

        // The band scrolls away with the page now (it used to pin under the nav),
        // so a close triggered from deep in the case study can leave its box above
        // the viewport. The fly-back still travels from the band's honest top —
        // it simply swoops in from above — but the accent-retract wash must not
        // start off-screen, so its reference edge is clamped to the scrollport's
        // actual top edge. That is the live bottom of the auto-sized navigation;
        // unlike --nav-height it stays correct when zoom or wrapped content makes
        // the bar taller. This reduces to the original math whenever the band is
        // at or below that line, i.e. every close from at or near the top.
        var scrollportTop = scrollport
            ? scrollport.getBoundingClientRect().top
            : cssMs('--nav-height', 0);
        var washBottom = Math.max(rect.bottom, scrollportTop + rect.height);
        retractBackground(
            { bottom: washBottom, height: rect.height },
            saved.top + rect.height
        );

        root.classList.add('is-work-flying-back');
        band.style.transform = 'translateY(' + (saved.top - rect.top) + 'px)';
    }

    // Delegated so the real link in the case-study rail can contain the SVG.
    document.addEventListener('click', function (event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var link = event.target.closest('[data-work-close]');
        if (!link) return;

        if (closing) {
            event.preventDefault();
            return;
        }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        event.preventDefault();
        closing = true;

        var saved = takeReturn();

        // Handed to the overview so it knows to fly the stack back in around
        // this row rather than rendering the feed assembled.
        /* The whole note travels, not just the index it used to carry.

           The index is all work-opening.js needs to fly the stack back, but the
           overview's pre-paint gate needs the geometry too, and for a reason the
           index cannot cover: the feed arrives hidden, so between this page's
           last frame — an accent band sitting at the row's position — and the
           frame work-opening.js reveals the row in, nothing paints there at all.
           That gap is the flash, and the band vanishing into it is the jump.

           With top, height and accent in hand the gate can paint that same band
           before first paint, out of the geometry it just landed on, and hold it
           until the real row is visible underneath. */
        /* Without geometry the note is still worth sending, carrying only this
           page's own URL. The band cannot be painted and nothing flies to a row
           offset, but the overview can find the row that links here and fly the
           rest of the stack in around it — which is the part of the close that
           was missing entirely when a case study was reached by any route but a
           click on the overview. */
        try {
            sessionStorage.setItem(
                'mk-work-returning',
                JSON.stringify(saved || { url: window.location.href })
            );
        } catch (e) {
            // Nothing to hand over; the overview loads plain.
        }

        rideToTop(function () {
            collapse(saved);

            // No transitionend: the band, the wash and the fade all finish at
            // their own moments. One timer off the declared duration keeps the
            // two files honest.
            window.setTimeout(function () {
                window.location.href = link.href;
            }, cssMs('--work-closing-duration-ms', 320) + 60);
        });
    });

    // Returning to a case study from the bfcache restores it mid-collapse.
    window.addEventListener('pageshow', function (event) {
        if (!event.persisted) return;
        closing = false;
        root.classList.remove('is-work-closing', 'is-work-flying-back');
        if (band) band.style.transform = '';

        var wash = document.querySelector('.gh-work-opening-wash');
        if (wash) wash.parentNode.removeChild(wash);
    });
}
